/** tm/components/GovTargetChips.tsx — 「전용 대상」 고르기 (2026-10-05f · 2026-10-05g 「업종 → 하위 업종」 재설계)
 *
 *  공고 등록 양식 · 검토함 패널(같은 GovProgramForm)에서 쓴다. 업종으로 알 수 없는 신분 · 자격만 — 화면은 한 목록(2026-10-05h), 저장 자리는 둘:
 *   ① 기업 유형 (분류 칸 category · 자동으로 찾은 것은 conditions.sec) — 확인서 기준, 매장운영 · 협동조합은 업체 자동
 *   ② 특수 자격 (conditions.sec) — 업체에 체크된 경우만(모르면 제외)
 *   전용 대상끼리는 «하나라도» · 업종 조건과는 «둘 다» (엔진 govMatch 전용 대상 8)
 *  업종 전용(소방 · 수소 …) · 전용 분야(식품 · 반려동물 …)는 위 「업종 → 하위 업종」으로 옮겼다(GovSubPicker).
 *  자동으로 켠 값(사업명 · 본문 · AI — sec_auto, 사업명 미리보기)은 「자동」 표시 · 끄면 sec_off 로 남겨 다시 켜지지 않게 한다.
 *  옛 값(분류 칸의 전용 분야 · 업종 전용 라벨)은 「옛 분류」 칩으로 보여 지울 수 있게 한다 — 매칭은 엔진이 새 하위 업종으로 그대로 읽는다.
 */
import type { ReactNode } from 'react';
import { C, T, R } from './ui';
import type { GovConditions } from '../types';
import { GOV_TYPES, GOV_SPECIAL_Q, GOV_SPECIAL_INFO, GOV_SUB_INFO, LEGACY_TO_KEYS, LEGACY_PROGRAM_KEYS, LEGACY_SPECIAL, OLD_SUB_KEYS, ANY_PARENT, isSubKey, parseSubKey, subLabel, fieldTip } from './GovTaxonomy';

export const TONE = {
  sub: { bd: '#AFA9EC', bg: '#EEEDFE', fg: '#3C3489' },
  types: { bd: '#7FB8A8', bg: '#E6F4EF', fg: '#0F5A47' },
  strict: { bd: '#E0A3A3', bg: '#FCEFEF', fg: '#8A2E2E' },
  /* 옛 화면 호환 */
  arts: { bd: '#D4A017', bg: '#FBF3DD', fg: '#7A5A00' },
  ind: { bd: '#8FB3D1', bg: '#E8F1F8', fg: '#1E4A6E' },
} as const;
export type ToneKey = keyof typeof TONE;
export const toneOf = (k: string): ToneKey => (isSubKey(k) || OLD_SUB_KEYS[k] ? 'sub' : k === TECH_KEY ? 'ind' : GOV_SPECIAL_Q.includes(k) || GOV_TYPES.includes(k) ? 'types' : 'arts');   // 전용 대상은 한 색(2026-10-05h) · 기술 기반은 업종 색 · 옛 키도 하위 업종 색(05m)
/** 전용 대상 안내 한 줄 (로움 · fphub 같은 글) */
export const GUIDE = '확인서·확인된 경우에만 체크 · 매장운영과 회사명의 협동조합은 자동 판정 · 재창업·재해·넷제로는 체크된 업체에만 연결 · 공장등록은 체크 안 한 제조업체를 「확인 필요」로';
export const FROM_LABEL: Record<string, string> = { title: '사업명', body: '본문', ai: 'AI 판정', old: '옛 분류', hint: '참고' };   // ai = 공고문 전체를 읽은 AI 판정 · hint = AI 가 확인하지 않은 낱말 판정(켜지 않음 · 2026-10-05o)
/** AI 확신 글 · 색 (2026-10-05o) */
export const CONF_LABEL: Record<string, string> = { high: '확신 높음', mid: '확신 중간', low: '확신 낮음' };
export const CONF_COLOR: Record<string, string> = { high: '#0F5A47', mid: '#8A5A00', low: '#8A2E2E' };
/** 자동 체크 기록(sec_auto)의 「기술 기반」 항목 키 (2026-10-05l) — conditions.tech 를 켠 근거 */
export const TECH_KEY = 'tech';
export const TECH_LABEL = '기술 기반 → 제조·연구개발·SW';
/** 칩 글 — 세부업종 키는 「제조업 › 식품」 · 「무관 › 수산」(2026-10-05q 업종 › 세부업종) · 기술 기반은 「기술 기반 → 제조·연구개발·SW」 */
export const targetText = (k: string): string => (isSubKey(k) || OLD_SUB_KEYS[k] ? subLabel(k) : k === TECH_KEY ? TECH_LABEL : k);

const uniq = (a: string[]) => a.filter((x, i) => a.indexOf(x) === i);
const listOf = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);

/** 칩 하나 — 켬(굵게) · 자동(「자동」 표시) · 자동인데 뺌(점선) */
export function TargetChip({ k, on, auto, off, onClick, size = 'md' }: { k: string; on: boolean; auto?: boolean; off?: boolean; onClick?: () => void; size?: 'sm' | 'md' }) {
  const t = TONE[toneOf(k)];
  const x = parseSubKey(k);
  const info = x ? { sub: '', tip: x.p === ANY_PARENT ? fieldTip(x.s) : (GOV_SUB_INFO[x.s] || '') } : GOV_SPECIAL_INFO[k];
  const title = [info?.tip, auto && on ? '✓ 사업명·본문에서 자동으로 체크 — 아니면 눌러서 빼세요' : '', off ? '자동으로 찾았지만 «아님»으로 뺀 값 — 누르면 다시 켭니다' : ''].filter(Boolean).join('\n\n') || undefined;
  return (
    <button type="button" onClick={onClick} title={title} aria-pressed={on}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4, padding: size === 'sm' ? '2px 8px' : '4px 10px', borderRadius: 6, fontSize: 11, lineHeight: 1.35,
        cursor: onClick ? 'pointer' : 'default', fontFamily: 'inherit',
        border: `1px ${off || (auto && on) ? 'dashed' : 'solid'} ${on ? t.bd : off ? C.borderDk : C.border}`, background: on ? t.bg : C.white,
        color: on ? t.fg : off ? C.muted : C.sub, fontWeight: on ? 600 : 400, textDecoration: off ? 'line-through' : 'none',
      }}>
      {targetText(k)}
      {info?.sub && size === 'md' && <span style={{ fontWeight: 400, opacity: 0.6 }}>{info.sub}</span>}
      {auto && on && <span style={{ fontSize: 10, fontWeight: 700, padding: '0 4px', borderRadius: 3, border: '1px solid currentColor', lineHeight: '14px', opacity: 0.85 }}>자동</span>}
    </button>
  );
}

interface Props {
  /** 분류 칸 라벨들 (일반 분류 · 기업 유형 · 옛 전용 분야 섞임) */
  category: string[];
  onCategory: (next: string[]) => void;
  conditions: GovConditions | null;
  onConditions: (next: GovConditions) => void;
  /** 저장 전 사업명으로 알아본 값 (POST /gov-programs/preview targets) */
  nameAuto?: string[];
}

export default function GovTargetPicker({ category, onCategory, conditions, onConditions, nameAuto = [] }: Props) {
  const c: GovConditions = conditions || {};
  const sec = listOf(c.sec), off = listOf(c.sec_off);
  const autoItems = c.sec_auto && Array.isArray(c.sec_auto.items) ? c.sec_auto.items : [];
  const autoKs = uniq([...autoItems.map(x => x.k), ...nameAuto]);
  const secOn = (k: string) => !off.includes(k) && (sec.includes(k) || nameAuto.includes(k));
  const isOn = (k: string) => category.includes(k) || secOn(k);
  const write = (nSec: string[], nOff: string[]) => {
    const next: GovConditions = { ...c };
    if (nSec.length) next.sec = uniq(nSec); else delete next.sec;
    if (nOff.length) next.sec_off = uniq(nOff); else delete next.sec_off;
    onConditions(next);
  };
  /** 기업 유형 — 직접 고르면 분류 칸(예전과 같은 자리) · 자동으로 켠 것은 conditions.sec 에서 끈다 */
  const toggleType = (k: string) => {
    if (isOn(k)) {
      if (category.includes(k)) onCategory(category.filter(x => x !== k));
      if (secOn(k)) write(sec.filter(x => x !== k), autoKs.includes(k) ? [...off, k] : off);
    } else { onCategory([...category, k]); if (off.includes(k)) write(sec, off.filter(x => x !== k)); }
  };
  const toggleSpecial = (k: string) => {
    if (secOn(k)) write(sec.filter(x => x !== k), autoKs.includes(k) ? [...off, k] : off);
    else write([...sec, k], off.filter(x => x !== k));
  };
  // 옛 값 — 분류 칸의 전용 분야 · 업종 전용 라벨, 전용 대상 칸의 옛 라벨(업종 전용 · 옛 특수 자격 이름)
  const legacyCat = category.filter(x => LEGACY_TO_KEYS[x] || LEGACY_SPECIAL[x]);
  const legacySec = sec.filter(x => LEGACY_TO_KEYS[x] || LEGACY_SPECIAL[x]);
  const pending = c.sec_auto && c.sec_auto.reviewed === false && autoItems.some(x => !off.includes(x.k));
  const found = autoItems.filter(x => !off.includes(x.k));

  const row = (title: string, hint: string, children: ReactNode) => (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr)', gap: 4, padding: '7px 0', borderTop: `1px dashed ${C.border}` }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: C.sub }}>{title} <span style={{ fontWeight: 400, color: C.muted }}>{hint}</span></div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>{children}</div>
    </div>
  );
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: C.sub, marginBottom: 2 }}>전용 대상 <span style={{ color: C.text }}>— 이런 업체만 신청할 수 있을 때만</span></div>
      <div style={{ fontSize: 10, color: C.muted, marginBottom: 4, lineHeight: 1.5 }}>
        {GUIDE} · 고른 것 중 <b style={{ color: C.sub, fontWeight: 600 }}>하나라도</b> · 업종 조건과는 <b style={{ color: C.sub, fontWeight: 600 }}>둘 다</b>
      </div>
      {found.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '2px 6px', margin: '2px 0 6px', padding: '6px 10px', borderRadius: R.sm, background: pending ? '#FFFBEB' : C.bgAlt, border: `1px solid ${pending ? '#FDE68A' : C.border}`, fontSize: T.xs, lineHeight: 1.5 }}>
          <span style={{ fontWeight: 700, color: pending ? '#92400E' : C.sub, whiteSpace: 'nowrap' }}>{pending ? '자동 체크 · 검토 대기' : '자동 체크 · 검토 완료'}</span>
          <span style={{ color: C.sub }}>{found.map(x => `${targetText(x.k)}(${FROM_LABEL[x.from] || x.from})`).join(' · ')}</span>
        </div>
      )}
      {/* 한 목록 (2026-10-05h 지도사) — 기업 유형(분류 칸) · 특수 자격(conditions.sec)은 저장 자리만 다르고 화면은 한 줄 */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: '7px 0', borderTop: `1px dashed ${C.border}` }}>
        {GOV_TYPES.map(k => (
          <TargetChip key={k} k={k} on={isOn(k)} auto={!category.includes(k) && autoKs.includes(k)} off={off.includes(k) && autoKs.includes(k)} onClick={() => toggleType(k)} />
        ))}
        {GOV_SPECIAL_Q.map(k => (
          <TargetChip key={k} k={k} on={secOn(k)} auto={autoKs.includes(k)} off={off.includes(k) && autoKs.includes(k)} onClick={() => toggleSpecial(k)} />
        ))}
      </div>
      {(legacyCat.length > 0 || legacySec.length > 0) && row('옛 분류', '— 매칭은 새 세부업종 · 이름으로 그대로 적용 · 누르면 지웁니다', [
        ...legacyCat.map(k => (
          <button key={`c${k}`} type="button" onClick={() => onCategory(category.filter(x => x !== k))}
            title={`예전 「${k}」 — 지금은 ${(LEGACY_PROGRAM_KEYS[k] || [LEGACY_SPECIAL[k]]).map(targetText).join(' · ')} 로 판정합니다. 위 업종 · 세부업종에서 다시 고르세요`}
            style={{ padding: '3px 9px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: `1px dashed ${C.borderDk}`, background: C.bgAlt, color: C.sub, fontFamily: 'inherit' }}>옛 「{k}」 ✕</button>
        )),
        ...legacySec.map(k => (
          <button key={`s${k}`} type="button" onClick={() => write(sec.filter(x => x !== k), off)}
            title={`예전 「${k}」 — 지금은 ${(LEGACY_PROGRAM_KEYS[k] || [LEGACY_SPECIAL[k]]).map(targetText).join(' · ')} 로 판정합니다`}
            style={{ padding: '3px 9px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: `1px dashed ${C.borderDk}`, background: C.bgAlt, color: C.sub, fontFamily: 'inherit' }}>옛 「{k}」 ✕</button>
        )),
      ])}
    </div>
  );
}
